import React, {
	FC,
	RefObject,
	useEffect,
	useReducer,
	useRef,
	useState
} from 'react'
import {
	RadioList,
	RequestFormPDFProps,
	RequestInputs,
	Steps as DefaultSteps,
	StepsUrbanism,
	RequestInputFiles
} from '@utils/request'
import requestForm from '@components/requestForms/request.form'
import { Collapse as ReactCollapse } from 'react-collapse'
import Collapse from '@components/ui/collapse'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import * as pageUtils from '../__forms.utils'
import { useAppSelector } from '@services/store'
import { joinClasses } from '@utils/styles'
import { AddressCoordinates, CustomDate } from '@services/models'
import { formatStrapiText, getFilesFromStore } from '@utils/methods'
import { getAddressByCoordinates } from '@components/ui/addressForm/__index.utils'
import * as utilsList from './__index.utils'
import { thirdStepSubStepsUrbanism } from '@templates/requests/create_tn_urbanism'

import { inputReducer, useInitialFormState } from './reducer.form'
import { inputFilesReducer, useInitialFilesState } from './reducerFiles.form'

import Step1TypeDeDemande from './Step1TypeDeDemande'
import Step2Localisation from './Step2Localisation'
import Step5InformationsTravaux from './Step5InformationsTravaux'
import Step7Documents from './Step7Documents'
import config from '@utils/config'
import Step3_1TypeUtilisation from './Step3_1TypeUtilisation'
import Step4_1RenovationOrDevelopment from './Step4_1RenovationOrDevelopment'
import Step6Branchement from './Step6Branchement'
import {
	isGR7_1RenovationOrDevelopment,
	isGR7_2RequestOccupation,
	isGR7_3HomeOfficeOrBusiness,
	isGR7_4InstallationOfEnseigne
} from './__index.utils'
import Step4_2RequestOccupation from './Step4_2RequestOccupation'
import Step3_3TypeUtilisation from './Step3_3TypeUtilisation'
import Step4_3HomeOfficeOrBusiness from './Step4_3HomeOfficeOrBusiness'
import Step3_4TypeUtilisation from './Step3_4TypeUtilisation'
import Step4_4InstallationOfEnseigne from './Step4_4InstallationOfEnseigne'

const IndustrialCommercialOrAgriculturalUsePermitForm: FC<
	RequestFormPDFProps & { subSteps?: thirdStepSubStepsUrbanism }
> = ({
	inputs,
	errors,
	subSteps,
	inputFiles,
	isSummaryStep,
	steps,
	toPrint,
	filesInputs,
	formPosition,
	setDisableNextButton,
	onCustomError,
	onSetInputFiles,
	onSetSubSteps,
	onFixError,
	onSetInputs,
	onSetFormInputs,
	onSetFormFilesInputs,
	setCurrentStep,
	onSetCurrentSubStep,
	setStepsList,
	setSteps,
	setInputFiles
}) => {
	const {
		authUser,
		pageAssets,
		UpdateRequestInputsArray,
		UpdateRequestInputFilesArray,
		UpdateRequestInputsErrorsArray
	} = requestForm()

	const answers = useAppSelector((state) => state.request.configs)
	const location = useAppSelector((state) => state.request.location)

	// ******************************
	// Initialize INPUTS state REDUCER
	const initialState = useInitialFormState()

	const [formInputs, dispatchFormInputs] = useReducer(
		inputReducer,
		initialState
	)

	const {
		whatToDo,
		// **********************
		buildingBatchNumber,

		isSameLocation,
		// **********************
		conditionalUseAuthorizationRequest
	} = formInputs

	const updateInput = (name, value) => {
		dispatchFormInputs({ type: 'UPDATE_INPUT', name, value })
		handleCrossReducerEffects(name, value)
	}

	const updateCheckBoxInput = (input, value) => {
		let newValue
		if (input.value?.includes(value)) {
			newValue = pageUtils.removeStrFromStrArray(value, input.value)
		} else {
			newValue = input.value == '' ? value : input.value + `, ${value}`
		}

		dispatchFormInputs({
			type: 'UPDATE_INPUT',
			name: input.name,
			value: newValue
		})
		handleCrossReducerEffects(input.name, newValue)
	}

	// ******************************
	// Initialize FILES state REDUCER
	const initialFilesState = useInitialFilesState(filesInputs)

	const [formFilesInputs, dispatchFormFilesInputs] = useReducer(
		inputFilesReducer,
		initialFilesState
	)

	// for validation of files inputs
	const [lastChangedInput, setLastChangedInput] = useState<RequestInputs>({
		name: 'lastChanged',
		label: '',
		labelKey: '',
		value: answers?.lastChanged || '',
		required: false,
		ref: useRef<HTMLSelectElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
	})

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs!, lastChangedInput))
	}, [lastChangedInput])

	const updateFileInput = (name, files) => {
		dispatchFormFilesInputs({ type: 'UPDATE_FILES', name, files })
		setLastChangedInput({ ...lastChangedInput, value: name })
		onFixError(errors.filter((error) => error !== name))
	}

	const updateUploadedFilesMap = (name, uploadedFilesMap) => {
		dispatchFormFilesInputs({
			type: 'UPDATE_UPLOADED_FILES_MAP',
			name,
			uploadedFilesMap
		})
	}

	const updateFileRequired = (name, required) => {
		dispatchFormFilesInputs({ type: 'UPDATE_REQUIRED', name, required })

		onFixError(errors.filter((error) => error !== name))
	}

	const updateFileRequiredByDocumentsConfig = (documentsStatusConfig) => {
		dispatchFormFilesInputs({
			type: 'UPDATE_REQUIRED_BY_DOCUMENTS_CONFIG',
			documentsStatusConfig
		})
	}

	useEffect(() => {
		onSetFormInputs && onSetFormInputs(formInputs)

		onFixError(
			errors.filter((error) => {
				const field = formInputs?.[error] ?? formFilesInputs?.[error]

				if (field == undefined) {
					return true
				}

				return (
					field?.required == true &&
					(field.value == '' || field.files?.size == 0)
				)
			})
		)
	}, [formInputs])

	useEffect(() => {
		onSetFormFilesInputs && onSetFormFilesInputs(formFilesInputs)
	}, [formFilesInputs])

	// ******************************
	// handle cross-reducer effects (inputs that affect other inputsFile)
	const handleCrossReducerEffects = (inputName, value) => {
		if (inputName === 'isLocationOwned') {
			// Procuration du propriétaire
			updateFileRequired('procurationInput', value == 'false' && !isGR7_2RequestOccupation(whatToDo.value))
		}

		if (inputName === 'existingMainBuildingIsShared') {
			// Autorisation des administrateurs
			updateFileRequired(
				'autorisationInput',
				value == 'true' && isGR7_1RenovationOrDevelopment(whatToDo.value)
			)
		}

		if (
			inputName === 'whatToDo' &&
			!isGR7_1RenovationOrDevelopment(whatToDo.value)
		) {
			// Autorisation des administrateurs !RESET
			updateFileRequired('autorisationInput', false)
		}

		if (inputName === 'whatToDo') {
			// Procuration du propriétaire !RESET
			updateFileRequired('procurationInput', !isGR7_2RequestOccupation(whatToDo.value))
		}
	}

	//----------------------------------------------------------------
	//--------------------- DETAILS 1 INPUTS -------------------------
	//----------------------------------------------------------------

	const radioListYesNo: RadioList[] = utilsList.radioListYesNo(pageAssets)

	//----------------------------------------------------------------
	//-------------------- SubStep DETAILS_2 INPUTS ------------------
	//----------------------------------------------------------------

	const [isAddressOutsideLaval, setIsAddressOutsideLaval] =
		useState<boolean>(false)

	const [showAdresseBlock, setShowAdresseBlock] = useState<boolean>(
		answers?.isSameLocation?.toString() == 'false' ? true : false
	)

	////////////////////////////////////////////////////////////////

	// ----------------------------------------------------------

	const [dummyOptionalInput, setDummyOptionalInput] = useState<RequestInputs>({
		name: 'dummyOptional',
		label: '',
		labelKey: '',
		value: '',
		required: false,
		ref: useRef<HTMLSelectElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
	})
	//----------------------------------------------------------------
	//----------------------- DOCUMENTS INPUTS -----------------------
	//----------------------------------------------------------------

	// ------------------- DETAILS INPUTS SETTERS --------------------

	// fetch cadastral number by coordinates
	const getCadastralNumber = (coordinates: AddressCoordinates) => {
		getAddressByCoordinates(coordinates).then((res) => {
			updateInput(
				buildingBatchNumber.name,
				res?.data?.features?.[0].properties.cadastre
			)

			// setBuildingBatchNumberInput({
			// 	...buildingBatchNumberInput,
			// 	value: res?.data?.features?.[0].properties.cadastre
			// })
		})
	}

	// check if address is outside Laval on component mount
	useEffect(() => {
		const coordinates: AddressCoordinates = new AddressCoordinates()
		coordinates.update(authUser?.profile?.address.coordinates!)

		if (coordinates.validate()) {
			setIsAddressOutsideLaval(false)
			getCadastralNumber(coordinates)
		} else {
			setIsAddressOutsideLaval(true)
		}
	}, [])

	useEffect(() => {
		setDisableNextButton && setDisableNextButton(true)

		if (isSameLocation.value == radioListYesNo[0].value) {
			if (isAddressOutsideLaval) {
				setDisableNextButton && setDisableNextButton(false)
			}
			setTimeout(() => {
				setShowAdresseBlock(false)
			}, 100)
		}

		if (isSameLocation.value == radioListYesNo[1].value) {
			setTimeout(() => {
				setShowAdresseBlock(true)
			}, 100)
		}
	}, [isSameLocation])

	useEffect(() => {
		if (subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4) {
			setDisableNextButton && setDisableNextButton(true)

			if (
				conditionalUseAuthorizationRequest.value?.toString() ==
				radioListYesNo[1].value
			) {
				setDisableNextButton && setDisableNextButton(false)
			}
		}
	}, [conditionalUseAuthorizationRequest, subSteps])

	useEffect(() => {
		if (
			subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2 &&
			isAddressOutsideLaval &&
			isSameLocation.value == radioListYesNo[0].value
		) {
			setDisableNextButton && setDisableNextButton(false)
		}
	}, [subSteps?.position])
	//------------------------------------------------------------

	//////////////////////////////////////////////////////////////////////////////////////////////
	//////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs!, dummyOptionalInput))
	}, [dummyOptionalInput])

	////////////////////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////
	// DOCUMENTS
	////////////////////////////////////////////////////////////////////////////////////////////////
	// FILES FROM STORE
	const initializeFilesInputsFromStore = () => {
		let inputFilesInit = { ...formFilesInputs }
		Object.keys(inputFilesInit).forEach((key) => {
			const inputFile = inputFilesInit[key]

			if (
				!!answers[inputFile.name] &&
				!!answers[inputFile.name].files &&
				inputFile.files.size == 0
			) {
				getFilesFromStore(inputFile.name, answers).then((result: Set<File>) => {
					inputFilesInit[key].files = result
					inputFilesInit[key].uploadedFilesMap = new Map<string, string>(
						Object.entries(answers[inputFile.name].uploadedFilesMap)
					)
				})
			}
		})

		dispatchFormFilesInputs({
			type: 'INITIALIZE_FILES_INPUTS_FROM_STORE',
			newState: inputFilesInit
		})
	}

	useEffect(() => {
		// check if exist answers (filesInputs) at the store, to intialize the input with the files already present
		// ----------------------------
		initializeFilesInputsFromStore()
	}, [])

	const onSetErrorFromFileComponent = (
		hasErrorInComponent: boolean,
		inputName: string
	) => {
		if (hasErrorInComponent) {
			if (!errors.includes(inputName)) {
				onFixError((oldErrors) => [...oldErrors, inputName])
			}

			return
		}

		onFixError(UpdateRequestInputsErrorsArray(errors, inputName))
	}

	const [disabledBlobUploader, setDisabledBlobUploader] = useState<
		boolean | undefined
	>(undefined)
	// const [lastChangedInput, setLastChangedInput] = useState<string | null>(thirdQuestion.name)
	const [fieldWarnings, setFieldWarnings] = useState<{
		[key: string]: boolean
	}>({})
	const [totalFilesCount, setTotalFilesCount] = useState(0)

	const calculateMaxFilesCount = () => {
		// On calcule bien le total ici

		let inputs = Object.values(formFilesInputs) as RequestInputFiles[]
		const total = inputs.reduce((sum, item) => sum + item.files.size, 0)

		setTotalFilesCount(total)

		const maxFilesReached = total >= config.request.blobFile.maxFilesCount

		if (maxFilesReached) {
			setDisabledBlobUploader(true)

			if (lastChangedInput.value) {
				// Active l'avertissement pour le champ concerné
				setFieldWarnings((prev) => ({
					...prev,
					[lastChangedInput.value]: true
				}))
			}
		} else {
			setDisabledBlobUploader(false)
			setFieldWarnings({})
			// setDisableNextButton?.(true)
		}
	}

	useEffect(() => {
		calculateMaxFilesCount()
	}, [])

	useEffect(() => {
		calculateMaxFilesCount()
	}, [formFilesInputs])

	// isGR7_1RenovationOrDevelopment
	// isGR7_2RequestOccupation
	const updateSteps = (whatToDo) => {
		// Update the steps based on the current form state

		// default steps
		let newSteps = [
			REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
			REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
			REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3,
			REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4,
			REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5,
			REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6,
			REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		]

		switch (whatToDo.toString()) {
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_1RenovationOrDevelopment:
				newSteps = [
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6,
					REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
				]
				break
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit.GR7_2RequestOccupation:
				newSteps = [
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
					// REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4,
					// REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5,
					// REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6,
					REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
				]
				break
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_3HomeOfficeOrBusiness:
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_4InstallationOfEnseigne:
				newSteps = [
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3,
					REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4,
					// REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5,
					// REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6,
					REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
				]
				break
		}

		onSetSubSteps({
			map: false,
			details_1: true,
			details_2: true,
			details_3: !isGR7_2RequestOccupation(whatToDo),
			details_4: true,
			details_5:
				!isGR7_2RequestOccupation(whatToDo) &&
				!isGR7_3HomeOfficeOrBusiness(whatToDo) &&
				!isGR7_4InstallationOfEnseigne(whatToDo),
			details_6:
				!isGR7_2RequestOccupation(whatToDo) &&
				!isGR7_3HomeOfficeOrBusiness(whatToDo) &&
				!isGR7_4InstallationOfEnseigne(whatToDo),
			requiredDocument: true,
			position:
				subSteps?.position && String(subSteps?.position) !== ''
					? subSteps?.position
					: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
			steps: newSteps
		})

		// default stepsList
		let newStepsList = [
			{
				title: formatStrapiText(pageAssets?.page_requestInfo_step1),
				subSteps: []
			},
			{
				title: formatStrapiText(pageAssets?.page_requestInfo_step2),
				subSteps: []
			},
			{
				title: formatStrapiText(pageAssets?.page_requestInfo_step3),
				subSteps: [
					pageAssets?.request_form_type_de_demandes,
					pageAssets?.request_form_request_location,
					pageAssets?.request_form_type_of_use,
					pageAssets?.request_form_renovations_informations,
					pageAssets?.request_form_information_on_the_work,
					pageAssets?.request_form_branching,
					pageAssets?.request_form_required_documents
				]
			},
			{
				title: formatStrapiText(pageAssets?.page_requestInfo_step4),
				subSteps: []
			},
			{
				title: formatStrapiText(pageAssets?.page_requestInfo_step5),
				subSteps: []
			}
		]

		switch (whatToDo.toString()) {
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_1RenovationOrDevelopment:
				newStepsList = [
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step1),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step2),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step3),
						subSteps: [
							pageAssets?.request_form_type_de_demandes,
							pageAssets?.request_form_request_location,
							pageAssets?.request_form_type_of_use,
							pageAssets?.request_form_renovations_informations,
							pageAssets?.request_form_information_on_the_work,
							pageAssets?.request_form_branching,
							pageAssets?.request_form_required_documents
						]
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step4),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step5),
						subSteps: []
					}
				]
				break
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit.GR7_2RequestOccupation:
				newStepsList = [
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step1),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step2),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step3),
						subSteps: [
							pageAssets?.request_form_type_de_demandes,
							pageAssets?.request_form_request_location,
							// pageAssets?.request_form_type_of_use,
							pageAssets?.request_form_occupation_informations,
							// pageAssets?.request_form_information_on_the_work,
							// pageAssets?.request_form_branching,
							pageAssets?.request_form_required_documents
						]
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step4),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step5),
						subSteps: []
					}
				]
				break
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_3HomeOfficeOrBusiness:
				newStepsList = [
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step1),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step2),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step3),
						subSteps: [
							pageAssets?.request_form_type_de_demandes,
							pageAssets?.request_form_request_location,
							pageAssets?.request_form_type_of_use,
							pageAssets?.request_form_occupation_informations,
							// pageAssets?.request_form_information_on_the_work,
							// pageAssets?.request_form_branching,
							pageAssets?.request_form_required_documents
						]
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step4),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step5),
						subSteps: []
					}
				]
				break

			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_4InstallationOfEnseigne:
				newStepsList = [
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step1),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step2),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step3),
						subSteps: [
							pageAssets?.request_form_type_de_demandes,
							pageAssets?.request_form_request_location,
							pageAssets?.request_form_type_of_use,
							pageAssets?.request_form_enseigne_informations,
							// pageAssets?.request_form_information_on_the_work,
							// pageAssets?.request_form_branching,
							pageAssets?.request_form_required_documents
						]
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step4),
						subSteps: []
					},
					{
						title: formatStrapiText(pageAssets?.page_requestInfo_step5),
						subSteps: []
					}
				]
				break
		}

		setStepsList && setStepsList(newStepsList)
	}

	useEffect(() => {
		updateSteps(whatToDo.value)
	}, [whatToDo])

	useEffect(() => {
		updateSteps(whatToDo.value)
	}, [])

	const backToForm = (
		step: number,
		subStep: string,
		currentSubStep: number
	) => {
		if (setCurrentStep && onSetSubSteps && subSteps && onSetCurrentSubStep) {
			setCurrentStep(step)
			onSetSubSteps({
				...subSteps,
				position: subStep as REQUEST_TN_DETAILS_STEPS_NAME
			})
			onSetCurrentSubStep(currentSubStep)
		}
	}

	// ! ************************************************************
	// ! ************************************************************

	const renderStep3FormTypeUtilisation = (isSummaryStep: boolean) => {
		switch (whatToDo.value) {
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_1RenovationOrDevelopment: {
				return (
					<Step3_1TypeUtilisation
						{...{
							pageAssets,
							pageUtils,
							formInputs,
							errors,
							updateInput,
							isSummaryStep: isSummaryStep,
							// ******

							formFilesInputs,
							updateFileInput,
							updateUploadedFilesMap,
							onSetErrorFromFileComponent,
							toPrint: toPrint
							// ******
						}}
					/>
				)
			}
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_3HomeOfficeOrBusiness: {
				return (
					<Step3_3TypeUtilisation
						{...{
							pageAssets,
							pageUtils,
							formInputs,
							errors,
							updateInput,
							isSummaryStep: isSummaryStep,
							toPrint: toPrint
							// ******
						}}
					/>
				)
			}
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_4InstallationOfEnseigne: {
				return (
					<Step3_4TypeUtilisation
						{...{
							pageAssets,
							pageUtils,
							formInputs,
							errors,
							updateInput,
							isSummaryStep: isSummaryStep,
							toPrint: toPrint
							// ******
						}}
					/>
				)
			}
		}
	}

	// ! ************************************************************

	const renderStep4FormCommercialPermit = (isSummaryStep: boolean) => {
		switch (whatToDo.value) {
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_1RenovationOrDevelopment: {
				return (
					<Step4_1RenovationOrDevelopment
						{...{
							pageAssets,
							pageUtils,
							formInputs,
							errors,
							updateInput,
							onFixError,
							isSummaryStep: isSummaryStep,
							// ******
							updateCheckBoxInput
						}}
					/>
				)
			}
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_2RequestOccupation: {
				return (
					<Step4_2RequestOccupation
						{...{
							pageAssets,
							pageUtils,
							formInputs,
							errors,
							updateInput,
							onFixError,
							isSummaryStep: isSummaryStep,
							// ******
							updateCheckBoxInput
						}}
					/>
				)
			}
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_3HomeOfficeOrBusiness: {
				return (
					<Step4_3HomeOfficeOrBusiness
						{...{
							pageAssets,
							pageUtils,
							formInputs,
							errors,
							updateInput,
							onFixError,
							isSummaryStep: isSummaryStep,
							// ******
							updateCheckBoxInput
						}}
					/>
				)
			}
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_4InstallationOfEnseigne: {
				return (
					<Step4_4InstallationOfEnseigne
						{...{
							pageAssets,
							pageUtils,
							formInputs,
							errors,
							updateInput,
							onFixError,
							isSummaryStep: isSummaryStep,
							// ******
							updateCheckBoxInput
						}}
					/>
				)
			}
		}
	}

	const getStep4Title = () => {
		switch (whatToDo.value) {
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_1RenovationOrDevelopment:
				return pageAssets?.request_form_renovations_informations
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit.GR7_2RequestOccupation:
				return pageAssets?.request_form_occupation_informations
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_3HomeOfficeOrBusiness:
				return pageAssets?.request_form_occupation_informations
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_4InstallationOfEnseigne:
				return pageAssets?.request_form_enseigne_informations
		}

		return pageAssets?.request_form_renovations_informations
	}

	// ! ************************************************************
	// ! ************************************************************

	if (isSummaryStep) {
		return (
			<section
				className={joinClasses([
					pageUtils.classes.noMargin,
					pageUtils.classes.collapse,
					pageUtils.classes.summarySubTitles,
					toPrint ? pageUtils.classes.toPrintTitle : '',
					'noMargin collapse summarySubTitles toPrintTitle'
				])}
				style={{ padding: '0px' }}
			>
				{subSteps?.details_1 && (
					<>
						<Collapse
							id={1}
							buttonText={pageAssets?.request_form_type_de_demandes}
							isOpen={toPrint}
							key={`request_form_type_de_demandes`}
							isToPrint={toPrint}
						>
							{!toPrint && (
								<div style={{ position: 'relative' }}>
									<a
										style={{
											display: toPrint ? 'none' : 'flex'
										}}
										className={joinClasses([
											pageUtils.classes.btn,
											pageUtils.classes.button,
											pageUtils.classes.buttonOutline,
											pageUtils.classes.summaryCollapseButton
										])}
										onClick={() =>
											backToForm(
												steps?.form!,
												REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
												1
											)
										}
									>
										{pageAssets?.label_modify}
									</a>
								</div>
							)}
							{toPrint && (
								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_type_de_demandes}
								</h4>
							)}
							<Step1TypeDeDemande
								{...{
									pageAssets,
									pageUtils,
									formInputs,
									errors,
									updateInput,
									onFixError,
									isSummaryStep
								}}
							/>

							<br />
							{toPrint && <hr />}
						</Collapse>
					</>
				)}

				{subSteps?.details_2 && (
					<>
						<Collapse
							id={2}
							buttonText={pageAssets?.request_form_request_location}
							isOpen={toPrint}
							key={`request_form_request_location`}
							isToPrint={toPrint}
						>
							{!toPrint && (
								<div style={{ position: 'relative' }}>
									<a
										style={{
											display: toPrint ? 'none' : 'flex'
										}}
										className={joinClasses([
											pageUtils.classes.btn,
											pageUtils.classes.button,
											pageUtils.classes.buttonOutline,
											pageUtils.classes.summaryCollapseButton
										])}
										onClick={() =>
											backToForm(
												steps?.form!,
												REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
												2
											)
										}
									>
										{pageAssets?.label_modify}
									</a>
								</div>
							)}
							{toPrint && (
								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_request_location}
								</h4>
							)}
							<Step2Localisation
								{...{
									pageAssets,
									pageUtils,
									formInputs,
									errors,
									updateInput,
									onFixError,
									isSummaryStep,
									// ******
									isAddressOutsideLaval,
									showAdresseBlock,
									inputs,
									onSetInputs,
									formFilesInputs,
									updateFileInput,
									updateUploadedFilesMap,
									onSetErrorFromFileComponent,
									toPrint
								}}
							/>
						</Collapse>
					</>
				)}

				{subSteps?.details_3 && (
					<>
						<Collapse
							id={3}
							buttonText={pageAssets?.request_form_type_of_use}
							isOpen={toPrint}
							key={`request_form_type_of_use`}
							isToPrint={toPrint}
						>
							{!toPrint && (
								<div style={{ position: 'relative' }}>
									<a
										style={{
											display: toPrint ? 'none' : 'flex'
										}}
										className={joinClasses([
											pageUtils.classes.btn,
											pageUtils.classes.button,
											pageUtils.classes.buttonOutline,
											pageUtils.classes.summaryCollapseButton
										])}
										onClick={() =>
											backToForm(
												steps?.form!,
												REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3,
												3
											)
										}
									>
										{pageAssets?.label_modify}
									</a>
								</div>
							)}
							{toPrint && (
								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_type_of_use}
								</h4>
							)}

							{whatToDo.value && renderStep3FormTypeUtilisation(true)}
						</Collapse>
					</>
				)}

				{subSteps?.details_4 && (
					<>
						<Collapse
							id={4}
							buttonText={getStep4Title()}
							isOpen={toPrint}
							key={`request_form_renovations_informations`}
							isToPrint={toPrint}
						>
							{!toPrint && (
								<div style={{ position: 'relative' }}>
									<a
										style={{
											display: toPrint ? 'none' : 'flex'
										}}
										className={joinClasses([
											pageUtils.classes.btn,
											pageUtils.classes.button,
											pageUtils.classes.buttonOutline,
											pageUtils.classes.summaryCollapseButton
										])}
										onClick={() =>
											backToForm(
												steps?.form!,
												REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4,
												isGR7_2RequestOccupation(whatToDo.value) ? 3 : 4
											)
										}
									>
										{pageAssets?.label_modify}
									</a>
								</div>
							)}
							{toPrint && (
								<h4 className={pageUtils.classes.h4}>{getStep4Title()}</h4>
							)}

							{/* Render the appropriate form for CommercialPermit work based on the value of 'whatToDo' */}
							{/***   ! important  ***/}
							{whatToDo.value && renderStep4FormCommercialPermit(true)}
						</Collapse>
					</>
				)}

				{subSteps?.details_5 && (
					<>
						<Collapse
							id={5}
							buttonText={pageAssets?.request_form_information_on_the_work}
							isOpen={toPrint}
							key={`request_form_information_on_the_work`}
							isToPrint={toPrint}
						>
							{!toPrint && (
								<div style={{ position: 'relative' }}>
									<a
										style={{
											display: toPrint ? 'none' : 'flex'
										}}
										className={joinClasses([
											pageUtils.classes.btn,
											pageUtils.classes.button,
											pageUtils.classes.buttonOutline,
											pageUtils.classes.summaryCollapseButton
										])}
										onClick={() =>
											backToForm(
												steps?.form!,
												REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5,
												5
											)
										}
									>
										{pageAssets?.label_modify}
									</a>
								</div>
							)}

							{toPrint && (
								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_information_on_the_work}
								</h4>
							)}

							<Step5InformationsTravaux
								{...{
									pageAssets,
									pageUtils,
									formInputs,
									errors,
									updateInput,
									onFixError,
									isSummaryStep
									// ******
								}}
							/>
						</Collapse>
					</>
				)}

				{subSteps?.details_6 && (
					<>
						<Collapse
							id={6}
							buttonText={pageAssets?.request_form_branching_aqueduc_egouts}
							isOpen={toPrint}
							key={`request_form_information_on_the_work`}
							isToPrint={toPrint}
						>
							{!toPrint && (
								<div style={{ position: 'relative' }}>
									<a
										style={{
											display: toPrint ? 'none' : 'flex'
										}}
										className={joinClasses([
											pageUtils.classes.btn,
											pageUtils.classes.button,
											pageUtils.classes.buttonOutline,
											pageUtils.classes.summaryCollapseButton
										])}
										onClick={() =>
											backToForm(
												steps?.form!,
												REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6,
												6
											)
										}
									>
										{pageAssets?.label_modify}
									</a>
								</div>
							)}

							{toPrint && (
								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_branching_aqueduc_egouts}
								</h4>
							)}

							<Step6Branchement
								{...{
									pageAssets,
									pageUtils,
									formInputs,
									errors,
									updateInput,
									onFixError,
									isSummaryStep,
									// ******
									updateCheckBoxInput
								}}
							/>
						</Collapse>
					</>
				)}

				{subSteps?.requiredDocument && (
					<>
						<Collapse
							id={7}
							buttonText={pageAssets?.request_form_required_documents}
							isOpen={toPrint}
							key={`request_form_required_documents`}
							isToPrint={toPrint}
						>
							{!toPrint && (
								<div style={{ position: 'relative' }}>
									<a
										style={{
											display: toPrint ? 'none' : 'flex'
										}}
										className={joinClasses([
											pageUtils.classes.btn,
											pageUtils.classes.button,
											pageUtils.classes.buttonOutline,
											pageUtils.classes.summaryCollapseButton
										])}
										onClick={() =>
											backToForm(
												steps?.form!,
												REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS,
												isGR7_2RequestOccupation(whatToDo.value)
													? 4
													: isGR7_3HomeOfficeOrBusiness(whatToDo.value) ||
													  isGR7_4InstallationOfEnseigne(whatToDo.value)
													? 5
													: 7
											)
										}
									>
										{pageAssets?.label_modify}
									</a>
								</div>
							)}
							{toPrint && (
								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_required_documents}
								</h4>
							)}

							<Step7Documents
								{...{
									pageAssets,
									pageUtils,
									formInputs,
									errors,
									isSummaryStep,
									// ******

									formFilesInputs,
									updateFileInput,
									updateUploadedFilesMap,
									onSetErrorFromFileComponent,

									// ******
									updateFileRequiredByDocumentsConfig,
									disabledBlobUploader,
									fieldWarnings,
									totalFilesCount,
									toPrint
								}}
							/>
						</Collapse>
					</>
				)}
			</section>
		)
	}

	return (
		<>
			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1 && (
				<ReactCollapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
					}
				>
					<section>
						<Step1TypeDeDemande
							{...{
								pageAssets,
								pageUtils,
								formInputs,
								errors,
								updateInput,
								onFixError
							}}
						/>
					</section>
				</ReactCollapse>
			)}

			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2 && (
				<ReactCollapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
					}
				>
					<section>
						<Step2Localisation
							{...{
								pageAssets,
								pageUtils,
								formInputs,
								errors,
								updateInput,
								onFixError,
								// ******
								isAddressOutsideLaval,
								showAdresseBlock,
								inputs,
								onSetInputs,
								formFilesInputs,
								updateFileInput,
								updateUploadedFilesMap,
								onSetErrorFromFileComponent
							}}
						/>
					</section>
				</ReactCollapse>
			)}

			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3 && (
				<ReactCollapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
					}
				>
					<section>
						{whatToDo.value && renderStep3FormTypeUtilisation(false)}
					</section>
				</ReactCollapse>
			)}

			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4 && (
				<ReactCollapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
					}
				>
					<section>
						{/* Render the appropriate form for CommercialPermit work based on the value of 'whatToDo' */}
						{/***   ! important  ***/}
						{whatToDo.value && renderStep4FormCommercialPermit(false)}
					</section>
				</ReactCollapse>
			)}

			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5 && (
				<ReactCollapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
					}
				>
					<section>
						<Step5InformationsTravaux
							{...{
								pageAssets,
								pageUtils,
								formInputs,
								errors,
								updateInput,
								onFixError
								// ******
							}}
						/>
					</section>
				</ReactCollapse>
			)}

			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6 && (
				<ReactCollapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
					}
				>
					<section>
						<Step6Branchement
							{...{
								pageAssets,
								pageUtils,
								formInputs,
								errors,
								updateInput,
								onFixError,
								// ******
								updateCheckBoxInput
							}}
						/>
					</section>
				</ReactCollapse>
			)}

			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS && (
				<ReactCollapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
					}
				>
					<section className={pageUtils.classes.sectionNoPadding}>
						<Step7Documents
							{...{
								pageAssets,
								pageUtils,
								formInputs,
								errors,
								// ******
								formFilesInputs,
								updateFileInput,
								updateUploadedFilesMap,
								onSetErrorFromFileComponent,
								// ******
								updateFileRequiredByDocumentsConfig,
								disabledBlobUploader,
								fieldWarnings,
								totalFilesCount
							}}
						/>
					</section>
				</ReactCollapse>
			)}
		</>
	)
}

export default IndustrialCommercialOrAgriculturalUsePermitForm
